<!-- This component is used to display the Foxit PDF viewer -->
<template>
	<div class="relative h-full w-full transition-all duration-200">
		<div
			id="pdf-ui-preview"
			class="h-full w-full bg-[#fbfbfd] px-2 pb-2 transition-opacity duration-300"
		></div>
	</div>
</template>

<script setup lang="ts">
import {
	CustomPreviewAppearance,
	FoxitPreviewFragments,
} from '../Foxit/FoxItCustomAppearance'
import Plan from '@/classes/Plan'
import { useFoxitManager } from '@/components/documents/Foxit/composables/FoxitManager'
import { useDocumentStore } from '@/stores/document'
import type { StoreDocument } from '@/types'
import {
	PDFViewCtrl,
	PDFUI,
} from '@foxitsoftware/foxit-pdf-sdk-for-web-library'
import preloadJrWorker from '@foxitsoftware/foxit-pdf-sdk-for-web-library/lib/preload-jr-worker.js'
import { captureException } from '@sentry/vue'
import { DateTime } from 'luxon'
import { onMounted, onUnmounted, watch } from 'vue'
import { useRouter } from 'vue-router'

const documentStore = useDocumentStore()
const LICENSE_KEY = import.meta.env.VITE_FOXIT_PDF_SDK_LICENSE_KEY
const LICENSE_SN = import.meta.env.VITE_FOXIT_PDF_SDK_LICENSE_SN
const router = useRouter()
const foxitManager = useFoxitManager()

let pdfui: PDFUI
let pdfViewer: PDFViewCtrl.PDFViewer

const props = defineProps<{
	planHolder?: Plan
}>()

const foxitReady = defineModel<boolean>('foxitReady')
const open = defineModel<boolean>('open')

onMounted(async () => {
	await prepareUiandViewer()
	foxitReady.value = true

	window.addEventListener('print-preview-trigger', printPreview)
})

onUnmounted(() => {
	window.removeEventListener('print-preview-trigger', printPreview)
})

watch(
	() => open.value,
	async (newVal) => {
		if (newVal) {
			await openPDF()
		}
	}
)

const printPreview = async () => {
	const doc = pdfViewer.getCurrentPDFDoc()
	const file = await doc?.getFile()
	const url = URL.createObjectURL(file as Blob)

	const routeData = router.resolve({
		path: '/print',
		query: { file: url },
	})

	window.open(routeData.href, '_blank')
	open.value = false
}

// This method preloads the FoxIt PDF SDK and initializes the PDFUI and PDFViewer.
const prepareUiandViewer = async (): Promise<void> => {
	const readyWorker = preloadJrWorker({
		workerPath: '/FoxitPDFSDKForWeb/lib/',
		enginePath: '/FoxitPDFSDKForWeb/lib/jr-engine/gsdk',
		fontPath: 'http://webpdf.foxitsoftware.com/webfonts/',
		licenseSN: LICENSE_SN,
		licenseKey: LICENSE_KEY,
	})
	pdfui = new PDFUI({
		viewerOptions: {
			libPath: '/FoxitPDFSDKForWeb/lib',
			jr: { readyWorker: readyWorker },
		},
		renderTo: '#pdf-ui-preview',
		appearance: CustomPreviewAppearance,
		// @ts-ignore incorrectly typed in Foxit SDK
		fragments: FoxitPreviewFragments,
	})
	pdfViewer = await pdfui.getPDFViewer()
	pdfViewer.setFormHighlightColor(0x00ffffff)
}

// This method opens a PDF document and sets the current document in the sessionDocumentsStore.
const openPDF = async () => {
	if (!props.planHolder) {
		return
	}

	let mergedDocument: StoreDocument | undefined
	try {
		mergedDocument = documentStore.getPlanHolderMergedDocument(props.planHolder)
	} catch (error) {
		console.log('Merged doc not found.')
	}

	try {
		if (!mergedDocument) {
			await foxitManager.mergeDocsForPlanHolder(props.planHolder)
			mergedDocument = documentStore.getPlanHolderMergedDocument(
				props.planHolder
			)
		}
		return pdfui.openPDFByFile(
			mergedDocument?.blob.slice(0) as unknown as Blob,
			{
				fileName: `${props.planHolder?.firstName}-${props.planHolder?.lastName}-${DateTime.now().toFormat('MM-dd-yyyy')}`,
			}
		)
	} catch (error) {
		captureException(error, {
			extra: {
				message: 'Error opening PDF',
			},
		})
	}
}
</script>

<style>
@reference "tailwindcss";
.fv__ui-dropdown.fv__ui-editable_zoom_dropdown {
	@apply w-31.25;
}
[component-name='toolbar-tabs'] {
	@apply m-0 py-1;
}
[component-name='goto-prev-page'],
[component-name='goto-next-page'] {
	width: 20px !important;
}
</style>
